import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Typography,
  Tag,
  Space,
  Popconfirm,
  Row,
  Col,
  Switch,
  TimePicker,
  Divider
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet icon'larını düzelt
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const { Title } = Typography;

// Harita click handler component
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

interface Station {
  _id: string;
  name: string;
  address: string;
  phone: string;
  location: {
    coordinates: [number, number];
  };
  services: Array<{
    type: string;
    name: string;
    isAvailable: boolean;
  }>;
  workingHours: {
    [key: string]: {
      open: string;
      close: string;
      isOpen: boolean;
    };
  };
  electricChargers?: Array<{
    _id?: string;
    type: string;
    power: number;
    connector: string;
    isAvailable: boolean;
    price: number;
  }>;
  isActive: boolean;
  isVerified: boolean;
}

const Stations: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStation, setEditingStation] = useState<Station | null>(null);
  const [form] = Form.useForm();
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number} | null>(null);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([41.0082, 28.9784]);

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    setLoading(true);
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStation = () => {
    setEditingStation(null);
    setSelectedLocation(null); // Yeni istasyon eklerken lokasyonu temizle
    form.resetFields();
    // Form'u boş değerlerle başlat
    form.setFieldsValue({
      electricChargers: []
    });
    setModalVisible(true);
  };

  const handleEditStation = (station: Station) => {
    setEditingStation(station);
    
    // Hizmetleri sadece type olarak al
    const serviceTypes = station.services?.map(service => service.type) || [];
    
    // Mevcut lokasyonu selectedLocation'a da set et
    if (station.location && station.location.coordinates) {
      setSelectedLocation({
        lat: station.location.coordinates[1],
        lng: station.location.coordinates[0]
      });
    }
    
    const formValues = {
      name: station.name,
      address: station.address,
      phone: station.phone,
      services: serviceTypes,
      latitude: station.location.coordinates[1],
      longitude: station.location.coordinates[0],
      electricChargers: station.electricChargers?.map(charger => ({
        ...charger,
        power: charger.power.toString(), // Form input'u için number'ı string'e çevir
        price: charger.price?.toString(), // Form input'u için number'ı string'e çevir
      })) || [],
    };
    
    // Çalışma saatlerini dayjs formatına çevir
    const workingHours: { [key: string]: any } = {};
    Object.keys(station.workingHours).forEach(day => {
      const dayHours = station.workingHours[day];
      workingHours[`${day}_open`] = dayHours.open ? dayjs(dayHours.open, 'HH:mm') : null;
      workingHours[`${day}_close`] = dayHours.close ? dayjs(dayHours.close, 'HH:mm') : null;
      workingHours[`${day}_isOpen`] = dayHours.isOpen;
    });
    
    form.setFieldsValue({
      ...formValues,
      ...workingHours
    });
    
    // ElectricChargers'ı form'a set et (Form.List için)
    console.log('Edit station - Backend\'den gelen electricChargers:', station.electricChargers);
    console.log('Edit station - formValues.electricChargers:', formValues.electricChargers);
    
    if (formValues.electricChargers && Array.isArray(formValues.electricChargers) && formValues.electricChargers.length > 0) {
      // Form.List için her bir charger'ı ayrı ayrı set et
      // Ant Design Form.List için array formatında olmalı
      form.setFieldValue('electricChargers', formValues.electricChargers);
      console.log('✅ electricChargers form\'a set edildi:', formValues.electricChargers);
    } else {
      form.setFieldValue('electricChargers', []);
      console.log('⚠️ electricChargers boş, form\'a boş array set edildi');
    }
    
    // Form değerlerini tekrar kontrol et
    setTimeout(() => {
      const formChargers = form.getFieldValue('electricChargers');
      console.log('Form\'dan okunan electricChargers:', formChargers);
    }, 100);
    
    setModalVisible(true);
  };

  const handleDeleteStation = async (stationId: string) => {
    try {
      await stationOwnerAPI.deleteStation(stationId);
      message.success('İstasyon silindi');
      fetchStations();
    } catch (error) {
      message.error('İstasyon silinemedi');
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setUserLocation({ lat, lng });
          setMapCenter([lat, lng]);
          message.success('Konumunuz alındı');
        },
        (error) => {
          message.error('Konum alınamadı. Varsayılan konum kullanılıyor.');
        }
      );
    } else {
      message.error('Tarayıcınız konum özelliğini desteklemiyor.');
    }
  };

  const handleMapSelect = () => {
    setSelectedLocation(null); // Önceki seçimi temizle
    setMapModalVisible(true);
    // Harita açıldığında kullanıcının konumunu al (ama seçili konum olarak atama)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setUserLocation({ lat, lng });
          setMapCenter([lat, lng]);
        },
        (error) => {
          // Varsayılan konum kullan
          setMapCenter([41.0082, 28.9784]);
        }
      );
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setSelectedLocation({ lat, lng });
    // Form'a da kaydet ki submit edilirken kullanılabilsin
    form.setFieldsValue({
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    });
    // Kullanıcıya geri bildirim ver
    message.info(`Seçildi: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    console.log('Lokasyon seçildi:', { lat, lng });
  };

  const handleMapCancel = () => {
    setMapModalVisible(false);
    setSelectedLocation(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Çalışma saatlerini düzenle
      const workingHours: { [key: string]: { open: string; close: string; isOpen: boolean } } = {};
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      
      days.forEach(day => {
        workingHours[day] = {
          open: values[`${day}_open`] ? values[`${day}_open`].format('HH:mm') : '06:00',
          close: values[`${day}_close`] ? values[`${day}_close`].format('HH:mm') : '22:00',
          isOpen: values[`${day}_isOpen`] !== undefined ? values[`${day}_isOpen`] : true
        };
      });

      // Hizmetleri düzenle
      const services = values.services?.map((service: string) => ({
        type: service,
        name: service === 'fuel' ? 'Yakıt' : 
              service === 'market' ? 'Market' :
              service === 'car_wash' ? 'Oto Yıkama' :
              service === 'restaurant' ? 'Restoran' :
              service === 'atm' ? 'ATM' :
              service === 'ev_charging' ? 'Elektrikli Araç Şarjı' : service,
        isAvailable: true
      })) || [];

      // Elektrikli araç şarj istasyonlarını düzenle
      console.log('Form submit - values.electricChargers:', values.electricChargers);
      console.log('Form submit - values.electricChargers tipi:', typeof values.electricChargers);
      console.log('Form submit - values.electricChargers uzunluğu:', values.electricChargers?.length);
      
      const electricChargers = values.electricChargers && Array.isArray(values.electricChargers) && values.electricChargers.length > 0
        ? values.electricChargers.map((charger: any) => {
            const processedCharger = {
              type: charger.type,
              power: parseFloat(charger.power) || 0,
              connector: charger.connector || '',
              isAvailable: charger.isAvailable !== undefined ? charger.isAvailable : true,
              price: parseFloat(charger.price) || 0
            };
            console.log('İşlenen şarj istasyonu:', processedCharger);
            return processedCharger;
          })
        : [];
      
      console.log('İşlenmiş electricChargers:', electricChargers);
      console.log('electricChargers uzunluğu:', electricChargers.length);

      // Lokasyon bilgisini kontrol et - önce selectedLocation, sonra form values, son olarak adres geocoding
      let locationData = null;
      
      console.log('Form submit - selectedLocation:', selectedLocation);
      console.log('Form submit - values.latitude:', values.latitude, 'values.longitude:', values.longitude);
      console.log('Form submit - editingStation:', editingStation ? 'Var' : 'Yok');
      
      // Yeni istasyon eklerken veya düzenlerken selectedLocation öncelikli
      if (selectedLocation) {
        // Haritadan seçilen lokasyon varsa onu kullan
        locationData = {
          type: 'Point',
          coordinates: [selectedLocation.lng, selectedLocation.lat]
        };
        console.log('✅ Lokasyon selectedLocation\'dan alındı:', locationData);
      } else if (values.latitude && values.longitude && parseFloat(values.latitude) !== 0 && parseFloat(values.longitude) !== 0) {
        // Form'dan gelen koordinatları kullan (0,0 değilse)
        locationData = {
          type: 'Point',
          coordinates: [parseFloat(values.longitude), parseFloat(values.latitude)]
        };
        console.log('✅ Lokasyon form values\'dan alındı:', locationData);
      } else {
        console.log('⚠️ Lokasyon yok, backend adres geocoding yapacak');
      }
      // Eğer hiç lokasyon yoksa, backend adres geocoding yapacak

      const stationData: any = {
        name: values.name,
        address: values.address,
        phone: values.phone,
        services,
        workingHours,
        // Lokasyon varsa ekle
        ...(locationData ? { location: locationData } : {})
      };
      
      // electricChargers her zaman gönderilmeli (boş array bile olsa)
      stationData.electricChargers = electricChargers;
      
      console.log('Gönderilen stationData:', JSON.stringify(stationData, null, 2));
      console.log('Gönderilen stationData.electricChargers:', stationData.electricChargers);

      if (editingStation) {
        await stationOwnerAPI.updateStation(editingStation._id, stationData);
        message.success('İstasyon güncellendi');
      } else {
        await stationOwnerAPI.addStation(stationData);
        message.success('İstasyon eklendi');
      }

      setModalVisible(false);
      setSelectedLocation(null); // Seçili lokasyonu temizle
      form.resetFields();
      fetchStations();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const columns = [
    {
      title: 'İstasyon Adı',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      ellipsis: true,
      render: (text: string) => (
        <div 
          style={{ 
            maxWidth: '160px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={text}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Adres',
      dataIndex: 'address',
      key: 'address',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <div 
          style={{ 
            maxWidth: '180px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={text}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (text: string) => (
        <div style={{ fontSize: '12px' }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Hizmetler',
      dataIndex: 'services',
      key: 'services',
      width: 150,
      render: (services: any[]) => (
        <div>
          {services.slice(0, 2).map((service, index) => (
            <Tag key={index} color={service.isAvailable ? 'green' : 'red'} style={{ fontSize: '10px', marginBottom: 2 }}>
              {service.name}
            </Tag>
          ))}
          {services.length > 2 && (
            <Tag color="default" style={{ fontSize: '10px' }}>+{services.length - 2}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Durum',
      key: 'status',
      width: 120,
      render: (record: Station) => (
        <div>
          <Tag color={record.isActive ? 'green' : 'red'} style={{ fontSize: '10px', marginBottom: 2 }}>
            {record.isActive ? 'Aktif' : 'Pasif'}
          </Tag>
          <Tag color={record.isVerified ? 'blue' : 'orange'} style={{ fontSize: '10px' }}>
            {record.isVerified ? 'Onaylı' : 'Beklemede'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (record: Station) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditStation(record)}
            style={{ fontSize: '11px' }}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu istasyonu silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteStation(record._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              style={{ fontSize: '11px' }}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          İstasyonlarım
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddStation}
        >
          Yeni İstasyon Ekle
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={stations}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} istasyon`,
            responsive: true
          }}
        />
      </Card>

      <Modal
        title={editingStation ? 'İstasyon Düzenle' : 'Yeni İstasyon Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedLocation(null); // Modal kapanırken lokasyonu temizle
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            electricChargers: []
          }}
        >
          <Form.Item
            name="name"
            label="İstasyon Adı"
            rules={[{ required: true, message: 'İstasyon adı gerekli' }]}
          >
            <Input placeholder="İstasyon adını girin" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Adres"
            rules={[{ required: true, message: 'Adres gerekli' }]}
          >
            <Input.TextArea placeholder="Tam adresi girin" rows={3} />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefon"
            rules={[{ required: true, message: 'Telefon gerekli' }]}
          >
            <Input placeholder="Telefon numarasını girin" />
          </Form.Item>

          <Form.Item label="Konum">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="latitude"
                  label="Enlem"
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Örn: 41.0082" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="longitude"
                  label="Boylam"
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Örn: 28.9784" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label=" " style={{ marginBottom: 0 }}>
                  <Button 
                    type="primary" 
                    icon={<EnvironmentOutlined />}
                    onClick={handleMapSelect}
                    style={{ width: '100%' }}
                  >
                    Haritadan Seç
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item
            name="services"
            label="Hizmetler"
            rules={[{ required: true, message: 'En az bir hizmet seçin' }]}
          >
            <Select
              mode="multiple"
              placeholder="Hizmetleri seçin"
              options={[
                { label: 'Yakıt', value: 'fuel' },
                { label: 'Market', value: 'market' },
                { label: 'Oto Yıkama', value: 'car_wash' },
                { label: 'Restoran', value: 'restaurant' },
                { label: 'ATM', value: 'atm' },
                { label: 'Elektrikli Araç Şarjı', value: 'ev_charging' },
              ]}
            />
          </Form.Item>

          <Divider>Çalışma Saatleri</Divider>
          
          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
            <Row key={day} gutter={16} style={{ marginBottom: 16 }}>
              <Col span={4}>
                <Form.Item
                  name={`${day}_isOpen`}
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
                <span style={{ marginLeft: 8 }}>
                  {day === 'monday' ? 'Pazartesi' :
                   day === 'tuesday' ? 'Salı' :
                   day === 'wednesday' ? 'Çarşamba' :
                   day === 'thursday' ? 'Perşembe' :
                   day === 'friday' ? 'Cuma' :
                   day === 'saturday' ? 'Cumartesi' : 'Pazar'}
                </span>
              </Col>
              <Col span={10}>
                <Form.Item
                  name={`${day}_open`}
                  label="Açılış"
                  style={{ marginBottom: 0 }}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name={`${day}_close`}
                  label="Kapanış"
                  style={{ marginBottom: 0 }}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          ))}

          <Divider>Elektrikli Araç Şarj İstasyonları (Opsiyonel)</Divider>
          
          <Form.List name="electricChargers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          {...restField}
                          name={[name, 'type']}
                          label="Şarj Tipi"
                          rules={[{ required: true, message: 'Şarj tipi seçiniz' }]}
                        >
                          <Select placeholder="Şarj tipini seçin">
                            <Select.Option value="AC Type 2 (Mennekes)">AC Type 2 (Mennekes) - Avrupa standart AC şarj</Select.Option>
                            <Select.Option value="CCS Combo 2">CCS Combo 2 - Avrupa standart DC hızlı şarj</Select.Option>
                            <Select.Option value="CHAdeMO">CHAdeMO - Japon standart DC hızlı şarj</Select.Option>
                            <Select.Option value="Tesla Supercharger">Tesla Supercharger - Tesla özel hızlı şarj</Select.Option>
                            <Select.Option value="AC Type 1 (J1772)">AC Type 1 (J1772) - Kuzey Amerika standart AC şarj</Select.Option>
                            <Select.Option value="CCS Combo 1">CCS Combo 1 - Kuzey Amerika standart DC hızlı şarj</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'power']}
                          label="Güç (kW)"
                          rules={[
                            { required: true, message: 'Güç giriniz' },
                            {
                              validator: (_, value) => {
                                if (!value) {
                                  return Promise.reject(new Error('Güç giriniz'));
                                }
                                const numValue = parseFloat(value);
                                if (isNaN(numValue) || numValue <= 0) {
                                  return Promise.reject(new Error('Güç 0\'dan büyük olmalı'));
                                }
                                return Promise.resolve();
                              }
                            }
                          ]}
                        >
                          <Input type="number" placeholder="Örn: 50" min="0.01" step="0.1" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'connector']}
                          label="Konnektör"
                          rules={[{ required: true, message: 'Konnektör tipini giriniz' }]}
                        >
                          <Input placeholder="Örn: Type 2" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'price']}
                          label="Fiyat (TL/kWh)"
                          rules={[
                            {
                              validator: (_, value) => {
                                if (!value || value === '') {
                                  return Promise.resolve(); // Fiyat opsiyonel
                                }
                                const numValue = parseFloat(value);
                                if (isNaN(numValue) || numValue < 0) {
                                  return Promise.reject(new Error('Fiyat 0 veya 0\'dan büyük olmalı'));
                                }
                                return Promise.resolve();
                              }
                            }
                          ]}
                        >
                          <Input type="number" placeholder="Örn: 5.50" min="0" step="0.01" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'isAvailable']}
                          label="Müsait"
                          valuePropName="checked"
                          style={{ marginTop: 30 }}
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Button 
                          type="link" 
                          danger 
                          onClick={() => remove(name)}
                          icon={<DeleteOutlined />}
                        >
                          Şarj İstasyonunu Kaldır
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Şarj İstasyonu Ekle
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Divider>Konum Bilgileri (Opsiyonel)</Divider>
          <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
            <p style={{ margin: 0, color: '#666' }}>
              <EnvironmentOutlined /> Koordinatları manuel girmek istemiyorsanız, sadece adres bilgisini girin. 
              Sistem otomatik olarak koordinatları bulacaktır.
            </p>
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingStation ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setSelectedLocation(null); // Modal kapanırken lokasyonu temizle
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Harita Seçimi Modal */}
      <Modal
        title="Konum Seçin"
        open={mapModalVisible}
        onCancel={handleMapCancel}
        footer={[
          <Button key="location" onClick={getCurrentLocation} icon={<EnvironmentOutlined />}>
            Konumuma Git
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            onClick={() => {
              if (selectedLocation) {
                // Form'a da kaydet ki submit edilirken kullanılabilsin
                form.setFieldsValue({
                  latitude: selectedLocation.lat.toFixed(6),
                  longitude: selectedLocation.lng.toFixed(6)
                });
                setMapModalVisible(false);
                message.success(`Konum kaydedildi: ${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`);
              } else {
                message.warning('Lütfen haritadan bir konum seçin');
              }
            }}
            disabled={!selectedLocation}
          >
            Konumu Onayla
          </Button>,
          <Button key="cancel" onClick={handleMapCancel}>
            İptal
          </Button>
        ]}
        width={900}
        style={{ top: 20 }}
        bodyStyle={{ padding: '16px', maxHeight: '70vh', overflow: 'auto' }}
      >
        <div style={{ height: '400px', border: '1px solid #d9d9d9', borderRadius: '6px', position: 'relative' }}>
          {mapModalVisible && (
            <MapContainer
              center={mapCenter}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
              key={`${mapCenter[0]}-${mapCenter[1]}`} // Haritayı yeniden render etmek için
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapClickHandler onLocationSelect={handleLocationSelect} />
              {userLocation && (
                <Marker 
                  position={[userLocation.lat, userLocation.lng]}
                  icon={L.divIcon({
                    className: 'user-location-marker',
                    html: '<div style="background-color: #1890ff; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                    iconSize: [20, 20],
                    iconAnchor: [10, 10]
                  })}
                />
              )}
              {selectedLocation && (
                <Marker 
                  position={[selectedLocation.lat, selectedLocation.lng]}
                  icon={L.divIcon({
                    className: 'selected-location-marker',
                    html: '<div style="background-color: #52c41a; width: 24px; height: 24px; border-radius: 50%; border: 4px solid white; box-shadow: 0 3px 8px rgba(0,0,0,0.4); animation: pulse 1.5s infinite;"></div>',
                    iconSize: [24, 24],
                    iconAnchor: [12, 12]
                  })}
                />
              )}
            </MapContainer>
          )}
        </div>
        <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}><strong>Nasıl Kullanılır:</strong></p>
              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '13px' }}>
                <li>Haritada istasyonunuzun konumunu bulun</li>
                <li>Konuma tıklayın</li>
                <li>Koordinatlar otomatik doldurulacak</li>
              </ul>
            </Col>
            <Col span={12}>
              {userLocation && (
                <div style={{ 
                  padding: '8px', 
                  backgroundColor: '#e6f7ff', 
                  borderRadius: '4px', 
                  marginBottom: '8px',
                  border: '1px solid #91d5ff'
                }}>
                  <p style={{ color: '#1890ff', margin: 0, fontSize: '13px' }}>
                    <strong>📍 Bulunduğunuz Konum:</strong><br />
                    {userLocation.lat.toFixed(6)}, {userLocation.lng.toFixed(6)}
                  </p>
                </div>
              )}
              {selectedLocation && (
                <div style={{ 
                  padding: '8px', 
                  backgroundColor: '#f6ffed', 
                  borderRadius: '4px',
                  border: '2px solid #52c41a',
                  animation: 'pulse 1.5s infinite'
                }}>
                  <p style={{ color: '#52c41a', margin: 0, fontSize: '13px', fontWeight: 'bold' }}>
                    <strong>✅ Seçilen Konum:</strong><br />
                    {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </p>
                </div>
              )}
              {!selectedLocation && !userLocation && (
                <p style={{ color: '#999', margin: 0, fontSize: '13px', fontStyle: 'italic' }}>
                  Henüz konum seçilmedi
                </p>
              )}
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default Stations;
